import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Constants } from '../constants/constants';
import { afterGetObjectCallbackInterface } from '../interfaces/interfaces';
import { ChangePassword } from '../models/change-password.model';
import { ConfirmEmail } from '../models/confirm-email.model';
import { ForgotPassword } from '../models/forgot-password.model';
import { ResetPassword } from '../models/reset-password.model';
import { UserLogin } from '../models/user-login.model';
import { UserPreSharedKeyResponse } from '../models/user-presharedkey-response.model';
import { UserPreSharedKey } from '../models/user-presharedkey.model';
import { UserTwoFactorLogin } from '../models/user-twofactor-login.model';
import { GenericBaseService } from '../services/generic-base.service';
import { Utils } from '../utils/utils';
import { UrlService } from '../services/url.service';

@Injectable()
export class AuthenticationService extends GenericBaseService {

  // Session data (access-token, expires, username, usericon, companyname, setup2fa) is stored in
  // localStorage rather than as cookies. It was previously stored via CookieService, but a single
  // cookie is capped at ~4096 bytes by every major browser, silently dropped (no exception, no
  // console warning) if it exceeds that -- and the access-token JWT embeds one claim per role, so an
  // admin account with many modules/roles enabled produces a token comfortably over that limit
  // (confirmed: 108 roles -> ~4.5KB token). The dropped cookie made isAuthenticated below evaluate to
  // false immediately after a fully successful login, which sent AuthenticationGuard straight back to
  // /account/login -- silently, since it was already the current route. localStorage has no such
  // per-item size ceiling (browsers commonly allow 5MB+) and, since this app already attaches the
  // token to API calls manually via an Authorization header (see AuthenticationInterceptor) rather
  // than relying on the browser to send cookies automatically, nothing here actually depended on
  // cookie semantics -- it was only ever being used as client-side storage.
  get isAuthenticated(): boolean {
    const token = localStorage.getItem('access-token');
    const expires = localStorage.getItem('expires');

    if (!token || !expires) {
      return false;
    }

    return new Date(expires) > new Date();
  }

  get isTwoFactorSetupActive(): boolean {
    return (localStorage.getItem('setup2fa') === 'true');
  }

  token: string;

  private readonly tokenHeader = new HttpHeaders({
    'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
    'No-Auth': 'True'
  });

  private sessionExpiryTimeout: any;

  constructor(
    protected httpClient: HttpClient,
    protected toastrService: ToastrService,
    private router: Router,
    // Retained only to clear the unrelated 'license' cookie on logout (see deleteSession below) --
    // everything else this service used to store via CookieService now lives in localStorage.
    private cookieService: CookieService,
    protected url: UrlService) {
    super('Authentication', httpClient, toastrService, url);
  }

  login(user: UserLogin, afterPostCallback?: Function, afterErrorCallback?: Function) {
    this.httpClient.post(this.getApiUrl('/login'), this.getBody(user), {
      headers: this.httpHheaders.set('No-Auth', 'True')
    })
      .subscribe(
        (data) => {
          if (afterPostCallback) {
            afterPostCallback(data);
          }
        },
        (error) => {
          if (afterErrorCallback) {
            afterErrorCallback(error);
          }

          Utils.handleSimpleError(this.toastrService, error);
        });
  }

  twoFactorLogin(userTwoFactorLogin: UserTwoFactorLogin, setupTwoFactorAuthentication: boolean, afterErrorCallback?: Function, customMessage?: string) {
    this.httpClient.post(this.getApiUrl('/token'), this.getUrlSearchParams(userTwoFactorLogin), {headers: this.tokenHeader})
      .subscribe(
        (data) => {
          this.setCookies(data, setupTwoFactorAuthentication);
          this.scheduleSessionExpiryLogout();

          if (setupTwoFactorAuthentication) {
            this.router.navigate(['/account/two-factor-setup']);
          } else {
            this.navigateToStartPage((data as any).startPageUri);
          }

          this.toastrService.success(customMessage);
        },
        (error) => {
          if (afterErrorCallback) {
            afterErrorCallback(error);
          }

          Utils.handleSimpleError(this.toastrService, error);
        });
  }

  private setCookies(data: object, setupTwoFactorAuthentication: boolean) {
    const token = (data as any).access_token;
    const username = (data as any).userName;
    const usericon = (data as any).userIcon;
    const companyname = (data as any).companyName;
    const expiration = data['.expires'];

    localStorage.setItem('access-token', token);
    localStorage.setItem('expires', expiration);
    localStorage.setItem('username', username);
    localStorage.setItem('usericon', usericon);
    localStorage.setItem('companyname', companyname);
    localStorage.setItem('setup2fa', setupTwoFactorAuthentication ? 'true' : 'false');
  }

  scheduleSessionExpiryLogout(): void {
    if (this.sessionExpiryTimeout) {
      clearTimeout(this.sessionExpiryTimeout);
    }

    const expires = localStorage.getItem('expires');

    if (!expires) {
      return;
    }

    const expiresAt = new Date(expires).getTime();

    if (isNaN(expiresAt)) {
      this.logout();
      return;
    }

    const delay = expiresAt - new Date().getTime();

    if (delay <= 0) {
      this.logout();
      return;
    }

    this.sessionExpiryTimeout = setTimeout(() => {
      this.toastrService.info('Your session has expired. Please log in again.');
      this.logout();
    }, delay);
  }

  // Route user to the custom start page or to the default start page.
  navigateToStartPage(startPageUri: string) {
    if (!Utils.isStringNullOrWhiteSpace(startPageUri)) {
      this.router.navigate([startPageUri]);
    } else {
      this.router.navigate([Constants.DefaultStartPageUri]);
    }
  }

  getUrlSearchParams(userTwoFactorLogin: UserTwoFactorLogin) {
    const body = new URLSearchParams();
    body.set('AuthenticationId', userTwoFactorLogin.AuthenticationId);
    body.set('AuthenticationCode', userTwoFactorLogin.AuthenticationCode);
    body.set('grant_type', 'password');

    return body.toString();
  }

  logout(redirectToLogin: boolean = true) {
    if (this.sessionExpiryTimeout) {
      clearTimeout(this.sessionExpiryTimeout);
      this.sessionExpiryTimeout = null;
    }

    this.deleteCookies();

    if (redirectToLogin && !this.router.url.startsWith('/account/login')) {
      this.router.navigateByUrl('/account/login');
    }
  }

  private deleteCookies() {
    localStorage.removeItem('access-token');
    localStorage.removeItem('expires');
    localStorage.removeItem('username');
    localStorage.removeItem('usericon');
    localStorage.removeItem('companyname');
    localStorage.removeItem('setup2fa');

    // 'license' is unrelated to this session data (set/read by LicenseService) and stays a cookie.
    this.cookieService.delete('license', '/');
  }

  changePassword(password: ChangePassword, afterPostCallback?: Function, customMessage?: string) {
    this.postNow('/changepassword', this.getBody(password), afterPostCallback, customMessage, false);
  }

  forgotPassword(forgotPassword: ForgotPassword, afterPostCallback?: Function, customMessage?: string) {
    this.postNow('/forgotpassword', this.getBody(forgotPassword), afterPostCallback, customMessage, false, false, true);
  }

  resetPassword(resetPassword: ResetPassword, afterPostCallback?: Function, customMessage?: string) {
    this.postNow('/resetpassword', this.getBody(resetPassword), afterPostCallback, customMessage, true, false, true);
  }

  confirmEmail(confirmEmail: ConfirmEmail, afterPostCallback?: Function, customMessage?: string) {
    this.postNow('/confirmemail', this.getBody(confirmEmail), afterPostCallback, customMessage, false);
  }

  getGeneratedPreSharedKey(afterGetObjectCallback: afterGetObjectCallbackInterface<UserPreSharedKey>) {
    this.getObject<UserPreSharedKey>('/presharedkey', null, afterGetObjectCallback);
  }

  savePreSharedKey(userPreSharedKeyResponse: UserPreSharedKeyResponse, afterPostCallback?: Function, customMessage?: string) {
    this.postNow('/presharedkey', this.getBody(userPreSharedKeyResponse), afterPostCallback, customMessage);
  }
}
